import { AssetTypeCode, type WorkflowJsonObject, type WorkspaceCode } from '@atlas/types'
import { Button, ListBoxItem, Select } from '@atlas/ui-primitives'
import { Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Eyebrow } from '@/components/editorial/PageHeader'
import type { WorkspaceAssignmentDTO } from '@/domain/workflow-admin.types'
import { WORKSPACE_CODE_LABEL } from '@/domain/workflow-labels'
import { ADOPTED_SECTION_WORKSPACE_CODES, WorkspaceTabsEditor } from './WorkspaceSectionsEditor'

const ASSET_TYPES = Object.values(AssetTypeCode)

function WorkspaceSelect({
	'aria-label': ariaLabel,
	selectedKey,
	placeholder,
	onSelectionChange,
}: {
	'aria-label': string
	/** `null`, never `undefined` — react-aria's `selectedKey` is `Key | null`; passing `undefined` makes it flip between
	 *  uncontrolled and controlled once a real selection lands, which is what silently drops the very first pick. */
	selectedKey: string | null
	placeholder?: string
	onSelectionChange: (code: WorkspaceCode) => void
}) {
	return (
		<Select
			aria-label={ariaLabel}
			selectedKey={selectedKey}
			placeholder={placeholder}
			onSelectionChange={(key) => onSelectionChange(key as WorkspaceCode)}
			className="flex-1"
		>
			{Object.entries(WORKSPACE_CODE_LABEL).map(([code, label]) => (
				<ListBoxItem key={code} id={code}>
					{label}
				</ListBoxItem>
			))}
		</Select>
	)
}

/** The section editor for a workspace assignment (default or override) — only when its workspace code has adopted section composition. */
function SectionsEditor({
	assignment,
	statusCode,
	onUpdateOptions,
}: {
	assignment: WorkspaceAssignmentDTO
	statusCode?: string
	onUpdateOptions: (assignmentId: string, options: WorkflowJsonObject) => void
}) {
	if (!ADOPTED_SECTION_WORKSPACE_CODES.includes(assignment.workspaceCode)) return null
	return (
		<WorkspaceTabsEditor
			workspaceCode={assignment.workspaceCode}
			options={assignment.options ?? {}}
			statusCode={statusCode}
			onChangeTabs={(tabs) =>
				onUpdateOptions(assignment.assignmentId, { ...(assignment.options ?? {}), tabs } as WorkflowJsonObject)
			}
		/>
	)
}

/**
 * The builder's per-status workspace editor — a default assignment
 * (`assetTypeCode: null`) plus per-asset-type overrides, both real
 * `workspace_assignment` rows (design.md D5). This is what actually decides
 * which primary-surface component a work order shows on this status; the
 * `workflow_status.workspace_default` column is not read by anything.
 */
export function WorkspaceAssignmentSection({
	assignments,
	statusCode,
	onSetDefault,
	onAddOverride,
	onUpdateOverride,
	onRemoveOverride,
	onUpdateOptions,
}: {
	assignments: readonly WorkspaceAssignmentDTO[]
	/** The status these assignments belong to — lets the tab editor derive the validation lane when no `options.lane` is authored yet. */
	statusCode?: string
	onSetDefault: (code: WorkspaceCode) => void
	/** `onError` (optional), called if the create is rejected, so the draft selection can be restored rather than silently lost. */
	onAddOverride: (assetTypeCode: string, code: WorkspaceCode, onError?: () => void) => void
	onUpdateOverride: (assignmentId: string, code: WorkspaceCode) => void
	onRemoveOverride: (assignmentId: string) => void
	onUpdateOptions: (assignmentId: string, options: WorkflowJsonObject) => void
}) {
	const defaultAssignment = assignments.find((a) => a.assetTypeCode === null)
	const overrides = assignments.filter((a) => a.assetTypeCode !== null)
	const overriddenTypes = new Set(overrides.map((o) => o.assetTypeCode))
	const availableTypes = ASSET_TYPES.filter((t) => !overriddenTypes.has(t))

	return (
		<div className="border-t border-ds-border pt-3">
			<Eyebrow className="mb-1">Workspace</Eyebrow>
			<p className="mb-2 font-mono text-ds-4xs text-ds-text-subtle">
				The primary surface a work order shows on this status — a default, plus overrides per asset type. No default
				resolved means the fail-safe fallback panel shows instead.
			</p>

			<label className="mb-1 block font-mono text-ds-4xs uppercase tracking-wide text-ds-text-subtle">Default</label>
			<WorkspaceSelect
				aria-label="Default workspace"
				selectedKey={defaultAssignment?.workspaceCode ?? null}
				placeholder="Not set"
				onSelectionChange={(code) =>
					defaultAssignment ? onUpdateOverride(defaultAssignment.assignmentId, code) : onSetDefault(code)
				}
			/>

			{defaultAssignment ? (
				<SectionsEditor assignment={defaultAssignment} statusCode={statusCode} onUpdateOptions={onUpdateOptions} />
			) : null}

			{overrides.length > 0 ? (
				<div className="mt-3 flex flex-col gap-2">
					<label className="block font-mono text-ds-4xs uppercase tracking-wide text-ds-text-subtle">
						Per-asset-type overrides
					</label>
					{overrides.map((o) => (
						<div key={o.assignmentId} className="flex flex-col gap-2">
							<div className="flex items-center gap-2">
								<span className="w-10 flex-none font-mono text-ds-3xs text-ds-text-subtle">{o.assetTypeCode}</span>
								<WorkspaceSelect
									aria-label={`Workspace override for ${o.assetTypeCode}`}
									selectedKey={o.workspaceCode}
									onSelectionChange={(code) => onUpdateOverride(o.assignmentId, code)}
								/>
								<button
									type="button"
									onClick={() => onRemoveOverride(o.assignmentId)}
									aria-label={`Remove override for ${o.assetTypeCode}`}
									className="text-ds-text-subtle"
								>
									<Trash2 className="h-3.5 w-3.5" />
								</button>
							</div>
							<SectionsEditor assignment={o} statusCode={statusCode} onUpdateOptions={onUpdateOptions} />
						</div>
					))}
				</div>
			) : null}

			{availableTypes.length > 0 ? (
				<NewOverrideDraft availableTypes={availableTypes} onAddOverride={onAddOverride} />
			) : null}
		</div>
	)
}

/** The new-override draft row — holds its own selection so a rejected create can restore it. */
function NewOverrideDraft({
	availableTypes,
	onAddOverride,
}: {
	availableTypes: string[]
	onAddOverride: (assetTypeCode: string, code: WorkspaceCode, onError?: () => void) => void
}) {
	const [draftType, setDraftType] = useState<string | null>(null)
	const [draftCode, setDraftCode] = useState<WorkspaceCode | null>(null)

	const addOverride = () => {
		if (!draftType || !draftCode) return
		const type = draftType
		const code = draftCode
		setDraftType(null)
		setDraftCode(null)
		onAddOverride(type, code, () => {
			setDraftType(type)
			setDraftCode(code)
		})
	}

	return (
		<div className="mt-3 flex items-center gap-2">
			<Select
				aria-label="New override's asset type"
				selectedKey={draftType}
				placeholder="Asset type"
				onSelectionChange={(key) => setDraftType(key as string)}
				className="flex-1"
			>
				{availableTypes.map((t) => (
					<ListBoxItem key={t} id={t}>
						{t}
					</ListBoxItem>
				))}
			</Select>
			<WorkspaceSelect
				aria-label="New override's workspace"
				selectedKey={draftCode}
				placeholder="Workspace"
				onSelectionChange={setDraftCode}
			/>
			<Button variant="secondary" size="sm" isDisabled={!draftType || !draftCode} onPress={addOverride}>
				＋ Add
			</Button>
		</div>
	)
}
